/**
 * Client-portal appointment cancellation helpers (ENG-3443).
 * Client cancellations always cancel the entire appointment.
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, AppointmentParticipant, Organization } from '@medplum/fhirtypes';
import {
  CLIENT_ABSENT_ATTENDANCE_STATUSES,
  normalizeCalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import { getParticipantAttendanceStatus } from '@unify/fhir-model/resources/appointment';
import {
  CANCELLATION_REASON_SYSTEM,
  MISSED_SESSION_FEE_EXT,
  PRACTICE_CANCELLATION_WINDOW_HOURS_EXT,
} from '@unify/fhir-model/constants';

function isActivePatientParticipant(participant: AppointmentParticipant): boolean {
  const ref = participant.actor?.reference;
  if (!ref?.startsWith('Patient/') && !ref?.startsWith('RelatedPerson/')) return false;
  const status = normalizeCalendarClientAttendanceStatus(getParticipantAttendanceStatus(participant));
  return !status || !CLIENT_ABSENT_ATTENDANCE_STATUSES.has(status);
}

/** Active Patient and RelatedPerson participants used by appointment list projections. */
export function countActivePatientParticipants(appointment: Appointment): number {
  return (appointment.participant ?? []).filter(isActivePatientParticipant).length;
}

/** Fee warning kind for the cancel modal (aligned with intake: late cancel / no-show → full fee). */
export type PortalCancelFeeKind = 'none' | 'late' | 'no-show';

export type PortalCancellationPolicy = {
  /** Hours before start when a cancel becomes a late cancel. */
  cutoffHours: number;
  /** Practice full missed-session / late-cancellation fee (formatted), e.g. "$150". */
  fullFeeLabel: string;
};

export const DEFAULT_PORTAL_CANCELLATION_POLICY: PortalCancellationPolicy = {
  cutoffHours: 24,
  fullFeeLabel: '$150',
};

export function resolvePortalCancelStatus(isLate: boolean): 'client-cancelled' | 'client-late-cancelled' {
  return isLate ? 'client-late-cancelled' : 'client-cancelled';
}

/** Hours from now until appointment start (negative when the session has already started). */
export function hoursUntilAppointmentStart(startIso: string, nowMs: number = Date.now()): number {
  return (new Date(startIso).getTime() - nowMs) / 3_600_000;
}

export function resolvePortalCancellationPolicy(org: Organization | null | undefined): PortalCancellationPolicy {
  const cutoffRaw = org?.extension?.find((e) => e.url === PRACTICE_CANCELLATION_WINDOW_HOURS_EXT)?.valueInteger;
  const feeRaw = org?.extension?.find((e) => e.url === MISSED_SESSION_FEE_EXT)?.valueString?.trim();
  return {
    cutoffHours:
      typeof cutoffRaw === 'number' && Number.isFinite(cutoffRaw) && cutoffRaw > 0
        ? cutoffRaw
        : DEFAULT_PORTAL_CANCELLATION_POLICY.cutoffHours,
    fullFeeLabel: feeRaw || DEFAULT_PORTAL_CANCELLATION_POLICY.fullFeeLabel,
  };
}

/**
 * Classify fee warning for a cancel attempt.
 * - After start → no-show fee (full fee)
 * - Within cancellation window before start → late cancellation fee (full fee)
 * - Outside window → no fee warning
 */
export function resolvePortalCancelFeeKind(
  startIso: string,
  policy: PortalCancellationPolicy,
  nowMs: number = Date.now(),
): PortalCancelFeeKind {
  const hoursUntil = hoursUntilAppointmentStart(startIso, nowMs);
  if (hoursUntil < 0) return 'no-show';
  if (hoursUntil < policy.cutoffHours) return 'late';
  return 'none';
}

export function portalCancelFeeWarningCopy(
  kind: PortalCancelFeeKind,
  policy: PortalCancellationPolicy,
): { title: string; message: string } | null {
  if (kind === 'late') {
    return {
      title: 'Late cancellation',
      message:
        `This appointment is within ${policy.cutoffHours} hours. Cancelling now will incur the full late cancellation fee of ${policy.fullFeeLabel}.`,
    };
  }
  if (kind === 'no-show') {
    return {
      title: 'No-show fee',
      message:
        `This session has already started or been missed. Cancelling now will incur the full no-show fee of ${policy.fullFeeLabel}.`,
    };
  }
  return null;
}

async function resolvePracticeOrganizationForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<Organization | undefined> {
  const patient = await medplum.readResource('Patient', patientId);
  const orgRef = patient.managingOrganization?.reference;
  if (!orgRef?.startsWith('Organization/')) return undefined;
  const orgId = orgRef.slice('Organization/'.length);
  if (!orgId) return undefined;
  return medplum.readResource('Organization', orgId);
}

export async function loadPortalCancellationPolicy(
  medplum: MedplumClient,
  patientId: string | undefined,
): Promise<PortalCancellationPolicy> {
  if (!patientId) return DEFAULT_PORTAL_CANCELLATION_POLICY;
  try {
    const org = await resolvePracticeOrganizationForPatient(medplum, patientId);
    return resolvePortalCancellationPolicy(org);
  } catch {
    return DEFAULT_PORTAL_CANCELLATION_POLICY;
  }
}

function buildAppointmentCancelationReason(
  code: 'client-cancelled' | 'client-late-cancelled',
): Appointment['cancelationReason'] {
  return {
    coding: [{ system: CANCELLATION_REASON_SYSTEM, code, display: code }],
  };
}

export function applyPortalCancellation(input: {
  appointment: Appointment;
  isLate: boolean;
}): Appointment {
  const statusCode = resolvePortalCancelStatus(input.isLate);
  return {
    ...input.appointment,
    status: 'cancelled',
    cancelationReason: buildAppointmentCancelationReason(statusCode),
  };
}

export async function submitPortalAppointmentCancellation(
  medplum: MedplumClient,
  input: {
    appointmentId: string;
    isLate: boolean;
  },
): Promise<Appointment> {
  const existing = await medplum.readResource('Appointment', input.appointmentId);
  const next = applyPortalCancellation({
    appointment: existing,
    isLate: input.isLate,
  });
  return medplum.updateResource(next);
}
